<template>
  <div class="wrapper source-sans">
    <h1 style="margin-top: 0;">Atlas Signature Tool</h1>

    <p>You can set your signature for Google Mail from this utility.</p>

    <template v-if="!authEmail">
      <p>First step is to authorize with you Google Mail account.</p>
      <input type="submit" value="Authorize with Google Mail" @click="gwsAuth">
    </template>

    <template v-else>
      <p>Now you can review and modify your email signature.</p>
      <label>Name</label>
      <input type="text" v-model="fullName">

      <label>Title</label>
      <input type="text" v-model="title" :disabled="!allowFullEdit">

      <label>Mobile Number</label>
      <input type="text" v-model="mobilePhone">

      <label>Work Phone</label>
      <input type="text" v-model="workPhone">

      <label>Booking Link</label>
      <input type="text" v-model="bookingLink">

      <label>Toll Free</label>
      <input type="text" v-model="tollFree" :disabled="!allowFullEdit">

      <label>Logo</label>
      <select v-model="brand" :disabled="!allowFullEdit">
        <option
          v-for="(brand, key) in brands"
          :key="key"
          :value="brand"
        >
          {{brand.name}}
        </option>
      </select>

      <label>Address</label>
      <input type="text" v-model="addressStreet" :disabled="!allowFullEdit">

      <label>City</label>
      <input type="text" v-model="addressLocality" :disabled="!allowFullEdit">

      <label>State / Region</label>
      <input type="text" v-model="addressRegion" :disabled="!allowFullEdit">

      <label>Postal Code</label>
      <input type="text" v-model="addressPostalCode" :disabled="!allowFullEdit">

      <!--
      <label>Show GBTA 2024 Banner</label>
      <input type="checkbox" v-model="showGbtaBanner" />
      -->

      <div class="preview">
        <p style="margin-top: 0;">Dear {{ fullName }},</p>
        <p>Welcome to the live preview feature, designed to give you a glimpse of how your email signature will appear within Google Mail. Take a moment to review and refine your signature using the fields provided above. If any mistakes slipped through, no worries – this is your opportunity to make adjustments.</p>

        <p>Once you've meticulously crafted your signature and are satisfied with the result, proceed to the bottom of this page and confidently press the submission button. Your polished and professional email signature will be seamlessly integrated into your Google Mail account, leaving a lasting impression on every email you send. Thank you for using our utility to enhance your email communication experience!</p>
        --
        <AtlasSignature
          ref="signature"
          :addressLocality="addressLocality"
          :addressPostalCode="addressPostalCode"
          :addressRegion="addressRegion"
          :addressStreet="addressStreet"
          :bookingLink="bookingLink"
          :brand="brand"
          :email="email"
          :fullName="fullName"
          :mobilePhone="mobilePhone"
          :shouldDisplayCell="shouldDisplayCell"
          :showGbtaBanner="showGbtaBanner"
          :title="title"
          :tollFree="tollFree"
          :workPhone="workPhone"
          />
      </div>
      <div v-if="success" class="success">Your Google Mail signature has been set. <a href="https://mail.google.com/mail/u/0/#inbox?compose=new" target="_blank">Go check it out!</a> </div>
      <div v-if="failure" class="failure">Uh oh! Something appears to have gone wrong. Please refresh the page and try agian.</div>
      <input type="submit" value="Make this my signature in Google Mail" @click="setSig">
    </template>
  </div>
</template>

<script>
import AtlasSignature from './components/AtlasSignature.vue'

function directroyLogoToBrand(directoryLogo) {
    const lookupTable = {
      'athome.png': 'at_home',
      'ati-leisure.gif': 'at_leisure',
      'ati-logo.gif': 'at_corp',
      'ati-logo-gif': 'at_corp',
      'ati-mi.gif': 'at_mi',
      'attg.png': 'attg',
      'pnt.png': 'pnt',
      'vcathome.png': 'vacay_athome'
    }

    return lookupTable[directoryLogo];
}

export default {
  name: 'App',
  data() {
    return {
      authEmail: null,
      allowFullEdit: false,
      submitting: false,
      success: false,
      failure: false,

      brands: {
        'attg': {
          logoSrc: 'https://images.atlastravel.com/signatures/attg.png',
          color: '#663399',
          name: 'Atlas Travel & Technology Group',
          website: 'https://atlastravelandtechnology.com/'
        },
        'at_corp': {
          logoSrc: 'https://images.atlastravel.com/signatures/ati-logo.gif',
          color: '#663399',
          name: 'Atlas Travel Global Travel Management',
          website: 'https://atlastravel.com/'
        },
        'at_home': {
          logoSrc: 'https://images.atlastravel.com/signatures/athome.png',
          color: '#663399',
          name: 'AT Home',
          website: 'https://athome.atlastravel.com/'
        },
        'at_leisure': {
          logoSrc: 'https://images.atlastravel.com/signatures/ati-leisure.gif',
          color: '#663399',
          name: 'Atlas Travel Leisure',
          website: 'https://vacations.atlastravel.com/'
        },
        'at_mi': {
          logoSrc: 'https://images.atlastravel.com/signatures/ati-mi.gif',
          color: '#663399',
          name: 'Atlas Travel Meetings & Incentives',
          website: 'https://atlasmeetingsandincentives.com/'
        },
        'vacay_athome': {
          logoSrc: 'https://images.atlastravel.com/signatures/vcathome.png',
          color: '#663399',
          name: 'Vacation & Home',
          website: 'https://vacations.atlastravel.com/'
        },
        'pnt': {
          logoSrc: 'https://static.primenumberstechnology.com/images/pnt200.png',
          color: '#00A3DD',
          name: 'Prime Numbers Technology',
          website: 'https://primenumberstechnology.com/'
        }
      },

      // Sig options...
      addressLocality: null,
      addressPostalCode: null,
      addressRegion: null,
      addressStreet: null,
      bookingLink: null,
      brand: {},
      email: null,
      fullName: null,
      mobilePhone: null,
      shouldDisplayCell: null,
      showGbtaBanner: null,
      title: null,
      tollFree: null,
      workPhone: null,
    };
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    this.allowFullEdit = urlParams.get('allowFullEdit') === 'true';

    this.addressLocality = urlParams.get('addressLocality');
    this.addressPostalCode = urlParams.get('addressPostalCode');
    this.addressRegion = urlParams.get('addressRegion');
    this.addressStreet = urlParams.get('addressStreet');
    this.authEmail = urlParams.get('authEmail');
    this.bookingLink = urlParams.get('bookingLink');
    this.brand = this.brands[directroyLogoToBrand(urlParams.get('sigLogo'))] || {};
    this.email = urlParams.get('email');
    this.fullName = urlParams.get('fullName');
    this.mobilePhone = this.shouldDisplayCell ? urlParams.get('mobilePhone') : null;
    this.shouldDisplayCell = urlParams.get('shouldDisplayCell') === 'true';
    this.showGbtaBanner = urlParams.get('showGbtaBanner') === 'true';
    this.title = urlParams.get('title');
    this.tollFree = urlParams.get('tollFree');
    this.workPhone = urlParams.get('workPhone');
  },
  methods: {
    async setSig() {
      if(this.submitting) return;

      this.submitting = true;
      this.success = false;
      this.failure = false;

      try {
        await gapi.client.request({
          path: `https://gmail.googleapis.com/gmail/v1/users/${this.authEmail}/settings/sendAs/${this.authEmail}`,
          method: 'put',
          body: JSON.stringify({ signature: this.$refs.signature.$el.outerHTML})
        })

        this.success = true;
      } catch(e) {
        this.failure = true;
      }

      this.submitting = false;
    },

    async gwsAuth() {
      await gapi.load('client');

      const client = google.accounts.oauth2.initTokenClient({
        client_id: '884869327228-14v4fii476ng9kvcos6f0cdkld573ri9.apps.googleusercontent.com',
        scope: 'https://www.googleapis.com/auth/gmail.settings.basic https://www.googleapis.com/auth/userinfo.email',
        callback: async () => {
          const userInfoResp = await gapi.client.request({ path: 'https://www.googleapis.com/oauth2/v2/userinfo' })
          this.authEmail = userInfoResp.result.email;
        },
      });

      client.requestAccessToken();
    }
  },
  components: {
    AtlasSignature
  }
}
</script>

<style scoped>

.source-sans {
  font-family: "Source Sans 3", sans-serif;
  font-optical-sizing: auto;
  font-style: normal;
}

input[type=text], select {
  width: 100%;
  padding: 6px 8px;
  margin: 4px 0;
  display: inline-block;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

input[type=submit] {
  width: 100%;
  background-color: #00a3dd;
  color: white;
  padding: 14px 20px;
  margin: 8px 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

input[type=submit]:hover {
  filter: brightness(92%);
}

.failure {
  background-color: red;
  color: white;
  padding: 14px 20px;
  margin: 8px 0;
  border: none;
  border-radius: 4px;

  a {
    color: white;
  }
}

.success {
  background-color: green;
}

.failure {
  background-color: red;
}

.success, .failure {
  color: white;
  padding: 14px 20px;
  text-align: center;
  margin: 8px 0;
  border: none;
  border-radius: 4px;

  a {
    color: white;
  }
}

div {
  border-radius: 5px;
  background-color: #f2f2f2;
  padding: 20px;
}

label {
  display: block;
  margin-top: 5px;
  font-size: 14px;
}

.preview {
  background: white;
  width: 540px;
  margin: 30px auto;
}

.wrapper {
  margin-bottom: 30px;
  padding: 15px 30px;
  width: 750px;
  margin: 20px auto;
}
</style>
